/* eslint-disable complexity */
import { FC, ReactNode, useMemo } from 'react';
import { Box, ButtonBase, SxProps, Theme, Typography } from '@mui/material';
import {
	EventPriceDto,
	EventPriceTypesEnum,
	EventTicketsReservationDto,
	EventTicketsTotalsWithReservationsDto,
	priceFormatter,
} from '@sweatpals/shared';
import AddIcon from '@mui/icons-material/AddRounded';
import RemoveIcon from '@mui/icons-material/RemoveRounded';
import { isNumber } from 'lodash-es';
import { priceTierCardStyles } from './price-tier-card.styles';
import { Button, useStyles, Showable, clsx, PromocodeIcon } from '@sweatpals/react-ui-kit';
import { usePriceTierData } from '@/modules/_core/hooks/events/use-price-tier-data.hook';

interface RadioButtonProps {
	checked: boolean;
	sx?: SxProps<Theme>;
	disabled?: boolean;
	onClick(): void;
}

const RadioButton: FC<RadioButtonProps> = ({ checked, disabled = false, sx, onClick }) => (
	<Box
		sx={[{ display: 'flex', alignItems: 'center', opacity: disabled ? 0.5 : 1 }, ...(Array.isArray(sx) ? sx : [sx])]}
	>
		{checked ? (
			<svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
				<rect y='0' width='18' height='18' rx='9' fill='white' fillOpacity='0.3' />
				<rect x='0.5' y='0.5' width='17' height='17' rx='8.5' stroke='white' strokeOpacity='0.2' />
				<circle cx='9' cy='9' r='5' fill='white' />
			</svg>
		) : (
			<Box onClick={onClick} sx={{ width: '18px', height: '18px', cursor: 'pointer' }}>
				<svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
					<rect x='0.5' y='0.5' width='17' height='17' rx='8.5' stroke='white' strokeOpacity='0.2' />
				</svg>
			</Box>
		)}
	</Box>
);

export interface PriceTierCardProps {
	currency: string;
	index: number;
	priceTier: EventPriceDto;
	// selected quantity before reservation
	quantity?: number;
	// event free spots minus selected quantity
	freeSpotsCount?: number;
	onlySingleAttendanceAllowed?: boolean;
	isPurchaseAllowedDueToSalesStartTime?: boolean;
	isSmallSize?: boolean;
	sx?: SxProps<Theme>;
	quantityButtonSx?: SxProps<Theme>;
	fontSx?: SxProps<Theme>;
	soldoutBadgeSx?: SxProps<Theme>;
	hideWaitlist?: boolean;
	hideQuantityButtons?: boolean;
	wrapTierName?: boolean;
	joinWaitlistButtonText?: ReactNode;
	eventTicketsCountAndSpots?: EventTicketsTotalsWithReservationsDto;
	userTicketReservations?: EventTicketsReservationDto[];
	disabled?: boolean;
	onAdd?(): void;
	onRemove?(): void;
	onJoinWaitlist?(tierId: string): void;
	onSubscribe?(): void;
}

const PriceTierCard: FC<PriceTierCardProps> = props => {
	const {
		currency,
		index,
		priceTier,
		sx,
		quantity,
		freeSpotsCount,
		isSmallSize = true,
		eventTicketsCountAndSpots,
		userTicketReservations,
		onlySingleAttendanceAllowed,
		isPurchaseAllowedDueToSalesStartTime,
		onAdd,
		onRemove,
		onSubscribe,
		onJoinWaitlist,
		joinWaitlistButtonText = 'Join Waitlist',
		hideWaitlist,
		hideQuantityButtons,
		disabled,
		quantityButtonSx,
		fontSx,
		soldoutBadgeSx,
		wrapTierName,
	} = props;
	const { id, priceAmount, tierName, tierDescription, tierTypeId } = priceTier;

	const { isTierSoldOut, isOutOfTickets, availableTickets } = usePriceTierData({
		priceTier,
		selectedQuantity: quantity,
		freeSpotsCount,
		eventTicketsCountAndSpots,
		userTicketReservations,
	});

	const styles = useStyles(priceTierCardStyles, isTierSoldOut);

	const isGroupTier = useMemo(() => (tierTypeId === EventPriceTypesEnum.Group), [tierTypeId]);
	const showWaitlist = isTierSoldOut && !hideWaitlist;
	const ticketsPerGroup = useMemo(() => (priceTier.ticketsPerGroup ?? 1), [priceTier.ticketsPerGroup]);

	const finalFontSx = clsx(fontSx, isTierSoldOut ? styles.soldoutFontSx((fontSx as any)?.color) : undefined);

	return (
		<Box
			sx={[
				{
					width: '100%',
					display: 'flex',
					borderRadius: '8px',
					padding: '16px',
					gap: '8px',
					backgroundColor: quantity ? `#FFFFFF1A` : undefined,
					border: `1px solid #FFFFFF${quantity ? `4D` : `1A`}`,
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Showable show={onlySingleAttendanceAllowed && !isSmallSize}>
				<RadioButton checked={!!quantity} disabled={disabled || isTierSoldOut} onClick={onSubscribe} />
			</Showable>
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					rowGap: '8px',
					columnGap: '16px',
					overflow: 'hidden',
					flexDirection: isSmallSize ? 'column-reverse' : 'row',
				}}
			>
				<Box display='flex' flexDirection='column' gap='8px' flex={1} overflow='hidden'
					sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
				>
					<Showable show={isGroupTier || (isTierSoldOut && hideWaitlist)}>
						<Box display='flex' alignItems='center' flexWrap='wrap' gap='10px'>
							<Showable show={isGroupTier}>
								<Box sx={clsx(styles.groupTierLabel, finalFontSx, { fontSize: isSmallSize ? '12px' : '14px' })}>
									<PromocodeIcon width={18} height={18} />
									<Typography variant='inherit'>
										{`Group ${isSmallSize ? '' : 'deal'}: ${ticketsPerGroup} tickets for `}
										{priceFormatter(priceAmount ? priceAmount * ticketsPerGroup : 0, { currency })}
									</Typography>
								</Box>
							</Showable>
							<Showable show={isTierSoldOut && hideWaitlist}>
								<Box sx={clsx(styles.tierSoldOut, finalFontSx, soldoutBadgeSx)}>⚡️Sold out!</Box>
							</Showable>
						</Box>
					</Showable>
					<Showable show={!isSmallSize}>
						<Typography sx={clsx(styles.tierName, wrapTierName ? { whiteSpace: 'initial' } : undefined, finalFontSx)}>
							{tierName}
						</Typography>
					</Showable>
					<Showable show={isSmallSize}>
						<Box display='flex' gap='8px' alignItems='center'>
							<Typography sx={clsx(styles.tierName, finalFontSx)}>{tierName}</Typography>
							<Showable show={isNumber(availableTickets) && availableTickets <= 10 && !isTierSoldOut && !isGroupTier}>
								<Typography sx={clsx(styles.ticketsAvailable, finalFontSx)}>{availableTickets} spots left</Typography>
							</Showable>
						</Box>
					</Showable>
					<Showable show={showWaitlist || tierDescription}>
						<Typography sx={clsx(styles.tierDescription, finalFontSx)}>
							{showWaitlist ? 'Join waitlist or try other dates or view other available events' : tierDescription}
						</Typography>
					</Showable>
				</Box>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: isSmallSize ? '10px' : '24px',
						justifyContent: isSmallSize ? 'space-between' : undefined,
					}}
				>
					<Showable show={isNumber(availableTickets) && availableTickets <= 10 && !isTierSoldOut && !isSmallSize && !isGroupTier}>
						<Typography sx={clsx(styles.ticketsAvailable, finalFontSx)}>{availableTickets} spots left</Typography>
					</Showable>
					<Box display='flex' gap='8px' alignItems='center'>
						<Showable show={onlySingleAttendanceAllowed && !showWaitlist && isPurchaseAllowedDueToSalesStartTime}>
							<RadioButton checked={!!quantity} disabled={disabled || isTierSoldOut} onClick={onSubscribe} />
						</Showable>
						<Typography sx={clsx(styles.price, finalFontSx)}>
							{priceFormatter(priceAmount ? priceAmount * ticketsPerGroup : 0, { currency })}
						</Typography>
					</Box>
					<Showable
						show={
							!onlySingleAttendanceAllowed &&
							!showWaitlist &&
							isPurchaseAllowedDueToSalesStartTime &&
							!hideQuantityButtons
						}
					>
						<Box sx={styles.quantityBox}>
							{quantity > 0 ? (
								<ButtonBase
									disabled={disabled}
									sx={clsx(styles.quantityButton, quantityButtonSx)}
									onClick={onRemove}
									id={`tier-${index + 1}-minus`}
								>
									<RemoveIcon />
								</ButtonBase>
							) : (
								// to prevent layout shift
								<div style={{ width: '24px' }} />
							)}
							<Typography sx={clsx(styles.quantity, finalFontSx)} id={`tier-${index + 1}-quantity`}>
								{quantity ? quantity / ticketsPerGroup : 0}
							</Typography>
							<ButtonBase
								disabled={disabled || isOutOfTickets || isTierSoldOut}
								sx={clsx(styles.quantityButton, quantityButtonSx)}
								onClick={onAdd}
								id={`tier-${index + 1}-plus`}
							>
								<AddIcon />
							</ButtonBase>
						</Box>
					</Showable>
					<Showable show={showWaitlist}>
						<Button
							transparent
							onClick={() => onJoinWaitlist(id)}
							sx={clsx(styles.joinWaitlistButton, finalFontSx)}
							disabled={disabled}
						>
							{joinWaitlistButtonText}
						</Button>
					</Showable>
				</Box>
			</Box>
		</Box>
	);
};

export default PriceTierCard;
